import { publicEnvConfig } from 'helpers/env';
import { bossiteUsersRestClient } from 'helpers/restclient';
import { applyParams, verifyDealershipIdParam } from './applyParams';
import { INPUT_KEYS } from 'components/DealDriver/constants';

const ADMIN_API_PREFIX = '/api/admin';
const BOSSSITE_BASE_URL = `${publicEnvConfig.RELATIVE_BASE_URL}/bosssite`;

export default class DealDriverService {
	static instance = new DealDriverService();

	_endpoints = {
		getAll: `${ADMIN_API_PREFIX}/bid_sales`,
		getBidSaleDetails: (bidSaleId) => `${ADMIN_API_PREFIX}/bid_sales/${bidSaleId}`,
		manageDeal: (id, entity) => `${ADMIN_API_PREFIX}/bid_sales/${id}/manage_deal/${entity.toLowerCase()}`,
		getScopeableEntities: `${ADMIN_API_PREFIX}/scopeable_entities`,
		getAllBidSalesDetails: `${ADMIN_API_PREFIX}/bid_sales_show_all`,
		tracks: `${ADMIN_API_PREFIX}/tracks`,
		getRunHistory: (id) => `${ADMIN_API_PREFIX}/bid_sales/${id}/run_history`,
		activities: (id) => `${ADMIN_API_PREFIX}/bid_sales/${id}/deal_working_activity_items`,
		loginAs: (userId) => `${BOSSSITE_BASE_URL}/users/${userId}/login_as`,
		vehicleServices: (vehicleId) => `${ADMIN_API_PREFIX}/vehicles/${vehicleId}/items?transportation=true`,
		paymentMethods: (vehicleId) => `${ADMIN_API_PREFIX}/vehicles/${vehicleId}/payment_methods`,
		getDealerships: `${ADMIN_API_PREFIX}/dealerships`,
		dropOffAddresses: (dealershipId) => `${ADMIN_API_PREFIX}/dealerships/${dealershipId}/dropoff_addresses`,
		vehiclePreview: (vehicleId) => `${ADMIN_API_PREFIX}/vehicles/${vehicleId}/preview`,
		buyFeeDiscountPreview: (vehicleId) => `${ADMIN_API_PREFIX}/vehicles/${vehicleId}/buy_fee_discount_preview`,
		vehicleWatchers: (vehicleId) => `${ADMIN_API_PREFIX}/vehicles/${vehicleId}/watchers`,
		offer: `${ADMIN_API_PREFIX}/offers`,
		searchRefreshAssessment: `${ADMIN_API_PREFIX}/search_refresh_assessment`,
		updateOffer: (offerId) => `${ADMIN_API_PREFIX}/offers/${offerId}`,
		getBidders: (bidSaleId) => `${ADMIN_API_PREFIX}/bid_sales/${bidSaleId}/bidders`,
		getMajorIssues: (bidSaleId) => `${ADMIN_API_PREFIX}/bid_sales/${bidSaleId}/major_issues`,
		getDealWorkerDetails: (dealWorkerId) => `${ADMIN_API_PREFIX}/admin_users/${dealWorkerId}`,
		getDirectors: `${ADMIN_API_PREFIX}/admin_users`,
		getOffers: (bidSaleId) => `${ADMIN_API_PREFIX}/bid_sales/${bidSaleId}/offers`,
		getPreviewOffers: (bidSaleId) => `${ADMIN_API_PREFIX}/bid_sales/${bidSaleId}/preview_offers`,
		getClosedMarketplaces: `${ADMIN_API_PREFIX}/closed_marketplaces`,
		closeDeal: (bidSaleId) => `${ADMIN_API_PREFIX}/bid_sales/${bidSaleId}/close_deal`,
		getPricingConditions: (vehicleId) => `${ADMIN_API_PREFIX}/vehicles/${vehicleId}/pricing_conditions`,
		getAccounts: `${ADMIN_API_PREFIX}/accounts`,
		getAccountsDetails: `${ADMIN_API_PREFIX}/accounts_details`,
		getDealershipsDetails: `${ADMIN_API_PREFIX}/dealerships_details`,
		getOffersCount: (dealershipId) => `${ADMIN_API_PREFIX}/dealerships/${dealershipId}/offers_count`,
		getUsers: `${ADMIN_API_PREFIX}/users`,
		getUsersDetails: `${ADMIN_API_PREFIX}/users_details`,
		createApprovalRequest: (bidSaleId) => `${ADMIN_API_PREFIX}/bid_sales/${bidSaleId}/approval_requests`,
	};

	constructor() {
		this.restClient = bossiteUsersRestClient;
	}

	loginAs = (userId) => {
		return this.restClient.post(
			this._endpoints.loginAs(userId),
			{},
			{
				withCredentials: true,
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
			},
		);
	};

	getAll = (config) => {
		const params = {
			page: config.page || 1,
		};

		const filtersWithoutPage = { ...(config.filters ?? {}) };
		delete filtersWithoutPage.page;

		applyParams(filtersWithoutPage, params);
		applyParams(config.sorting ?? {}, params);

		verifyDealershipIdParam(config.filters, params);

		return this.restClient.get(this._endpoints.getAll, params);
	};

	getBidSaleDetails = (bidSaleId) => {
		const endpoint = this._endpoints.getBidSaleDetails(bidSaleId);
		return this.restClient.get(endpoint);
	};

	manageDeal = (id, entity, data) => {
		const endpoint = this._endpoints.manageDeal(id, entity);

		const params = {};

		applyParams(data, params, {
			allowZeroKeys: [INPUT_KEYS.PRICE],
		});

		return this.restClient.post(endpoint, params);
	};

	getScopeableEntities = (config) => {
		const endpoint = `${this._endpoints.getScopeableEntities}?page=${config.page || 1}&name=${config.name}`;

		return this.restClient.get(endpoint);
	};

	getAllBidSalesDetails = (bidSalesIds) => {
		if (!Array.isArray(bidSalesIds)) {
			throw new Error('Expected bidSalesIds to be an array.');
		}

		if (bidSalesIds.length === 0) {
			throw new Error('bidSalesIds array cannot be empty.');
		}

		const ids = bidSalesIds.join(',');

		const endpoint = `${this._endpoints.getAllBidSalesDetails}?bid_sales_ids=${ids}`;

		return this.restClient.get(endpoint);
	};

	eventTracking = (config) => {
		const endpoint = this._endpoints.tracks;

		const params = {
			event: config.event,
		};

		if (config.vehicleId) {
			params.vehicle_id = config.vehicleId;
		}

		return this.restClient.post(endpoint, params);
	};

	getRunHistory = (id) => {
		const endpoint = this._endpoints.getRunHistory(id);
		return this.restClient.get(endpoint);
	};

	getNextActivitiesPage = (bidSaleId, page) => {
		const endpoint = this._endpoints.activities(bidSaleId);
		const params = { page: page + 1 };

		return this.restClient.get(endpoint, params);
	};

	getVehicleServices = async ({ vehicleId, isAuction, dropoffAddressId, amount, dealershipId, userId }) => {
		const endpoint = this._endpoints.vehicleServices(vehicleId);
		const params = { buyer_dealership_id: dealershipId };
		if (isAuction) params.context = 'timed_bid';
		if (dropoffAddressId) params.dropoff_address_id = dropoffAddressId;
		if (amount) params.amount = amount;
		if (userId) params.user_id = userId;

		return this.restClient.get(endpoint, params);
	};

	getPaymentMethods = (vehicleId, dealership, userId = undefined) => {
		const endpoint = this._endpoints.paymentMethods(vehicleId);
		return this.restClient.get(endpoint, {
			dealership_id: dealership,
			...(userId && { user_id: userId }),
		});
	};

	getDealerships = (query, accountIds = undefined, includeIdAndUser = false, userIds = undefined) => {
		const params = {
			name: query,
			...(accountIds && { account_id: accountIds }),
			...(includeIdAndUser && { include_id_and_user: includeIdAndUser }),
			...(userIds && { user_id: userIds }),
		};
		return this.restClient.get(this._endpoints.getDealerships, params);
	};

	getDropOffAddresses = (dealershipId) => {
		const endpoint = this._endpoints.dropOffAddresses(dealershipId);
		return this.restClient.get(endpoint, {});
	};

	getVehiclePreview = (vehiclePreviewPayload, dropoffAddressId, dealershipId) => {
		const { vehicleId, ...payload } = vehiclePreviewPayload;
		const endpoint = this._endpoints.vehiclePreview(vehicleId);
		return this.restClient.get(endpoint, {
			...payload,
			dropoff_address_id: dropoffAddressId,
			dealership_id: dealershipId,
		});
	};

	getVehicleWatchers = (vehicleId) => {
		const endpoint = this._endpoints.vehicleWatchers(vehicleId);
		return this.restClient.get(endpoint);
	};

	makeAnOffer = (offerPayload) => {
		const endpoint = this._endpoints.offer;
		return this.restClient.post(endpoint, offerPayload);
	};

	getSearchRefreshAssessment = ({ page, filters, sorting, bidSaleIds }) => {
		const searchFilters = { page: page || 1 };

		applyParams(filters ?? {}, searchFilters);
		applyParams(sorting ?? {}, searchFilters);

		const queryParams = new URLSearchParams();

		// Add bid_sale_ids[]=20&bid_sale_ids[]=21
		bidSaleIds.forEach((id) => {
			queryParams.append('bid_sale_ids[]', id);
		});

		// Add search_filters[...]
		Object.entries(searchFilters).forEach(([key, value]) => {
			queryParams.append(`search_filters[${key}]`, value);
		});

		const queryString = queryParams.toString();
		const endpoint = `${this._endpoints.searchRefreshAssessment}?${queryString}`;

		return this.restClient.get(endpoint);
	};

	updateOffer = (offerId, offerPayload) => {
		return this.restClient.put(this._endpoints.updateOffer(offerId), offerPayload);
	};

	getBidders = (vehicleId) => {
		return this.restClient.get(this._endpoints.getBidders(vehicleId));
	};

	getMajorIssues = (bidSaleId) => {
		return this.restClient.get(this._endpoints.getMajorIssues(bidSaleId));
	};

	getDealWorkerDetails = (dealWorkerId) => {
		return this.restClient.get(this._endpoints.getDealWorkerDetails(dealWorkerId));
	};

	getDirectors = ({ name, page = 1, perPage = 10 } = {}) => {
		return this.restClient.get(this._endpoints.getDirectors, {
			director_level: true,
			page,
			per_page: perPage,
			...(name ? { name } : {}),
		});
	};

	getOffers = (bidSaleId) => {
		return this.restClient.get(this._endpoints.getOffers(bidSaleId));
	};

	getPreviewOffers = (bidSaleId) => {
		return this.restClient.get(this._endpoints.getPreviewOffers(bidSaleId));
	};

	getClosedMarketplaces = () => {
		return this.restClient.get(this._endpoints.getClosedMarketplaces);
	};

	closeDeal = (bidSaleId, payload) => {
		return this.restClient.post(this._endpoints.closeDeal(bidSaleId), payload);
	};

	getAccounts = (config) => {
		const { page = 1, name, dealershipIds, userIds } = config;
		let params = { page, name };
		if (dealershipIds) params['dealership_id'] = dealershipIds;
		if (userIds) params['user_id'] = userIds;

		return this.restClient.get(this._endpoints.getAccounts, params);
	};

	getAccountsDetails = (ids) => {
		const endpoint = `${this._endpoints.getAccountsDetails}?ids=${ids}`;

		return this.restClient.get(endpoint);
	};

	getUsers = (config) => {
		const { page = 1, name, dealershipIds, accountIds, perPage } = config;
		let params = { page, name };
		if (dealershipIds) params['dealership_id'] = dealershipIds;
		if (accountIds) params['account_id'] = accountIds;
		if (perPage) params['per_page'] = perPage;

		return this.restClient.get(this._endpoints.getUsers, params);
	};

	getUsersDetails = (ids) => {
		const endpoint = `${this._endpoints.getUsersDetails}?ids=${ids}`;

		return this.restClient.get(endpoint);
	};

	getDealershipsDetails = (ids) => {
		const endpoint = `${this._endpoints.getDealershipsDetails}?ids=${ids}`;

		return this.restClient.get(endpoint);
	};

	getPricingConditions = (vehicleId) => this.restClient.get(this._endpoints.getPricingConditions(vehicleId));

	getOffersCount = (id, dealWorkingSide) => {
		const endpoint = this._endpoints.getOffersCount(id);

		return this.restClient.get(endpoint, { deal_working_side: dealWorkingSide });
	};

	getBuyFee = (vehicleId, dealershipId, userId, amount) => {
		const endpoint = this._endpoints.buyFeeDiscountPreview(vehicleId);

		return this.restClient.get(endpoint, {
			dealership_id: dealershipId,
			user_id: userId,
			amount,
		});
	};

	createApprovalRequest = (bidSaleId, approvalRequestPayload) => {
		const endpoint = this._endpoints.createApprovalRequest(bidSaleId);

		return this.restClient.post(endpoint, {
			approval_request: approvalRequestPayload,
		});
	};
}
